import {useCallback,useEffect,useState} from "react";
import {Link} from "react-router-dom";
import {ShieldCheck,Phone,MessageSquare,CalendarClock,GitBranch} from "lucide-react";
import api,{formatApiErrorDetail} from "@/lib/api";

const input="mt-1 block w-full min-w-0 rounded-md border bg-background px-3 py-2 text-sm";
const control="inline-flex min-h-10 items-center justify-center rounded-md border px-3 py-2 text-sm font-semibold disabled:opacity-40";
const INITIAL={agency_call_permission:false,agency_sms_permission:false,
  evidence_reference:"",verification_method:"inbound_call",
  exact_sms_disclosure:"",exact_call_disclosure:""};

export default function ExclusiveAgencyLeads() {
  const [rows,setRows]=useState([]);
  const [forms,setForms]=useState({});
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");

  const reload=useCallback(async()=>{
    const {data}=await api.get("/marketplace/agency-leads");
    setRows(data||[]);
  },[]);
  useEffect(()=>{reload().catch(()=>setError("Unable to load exclusive agency inquiries."))
    .finally(()=>setLoading(false));},[reload]);

  const form=id=>forms[id]||INITIAL;
  const update=(id,key,value)=>setForms(current=>({
    ...current,[id]:{...INITIAL,...current[id],[key]:value}
  }));
  const review=async lead=>{
    const payload=form(lead.id);
    if(busy||(payload.agency_sms_permission && payload.exact_sms_disclosure.trim().length<30)
      ||(payload.agency_call_permission && payload.exact_call_disclosure.trim().length<30)
      ||payload.evidence_reference.trim().length<10)return;
    if(!window.confirm("Record this agency-specific contact permission review? It will not send a message or place a call. Only documented consent permits subsequent manual outreach."))return;
    setBusy(lead.id);setError("");setNotice("");
    try{
      await api.post(`/marketplace/agency-leads/${lead.id}/contact-review`,payload);
      setNotice("Agency permission review recorded. No calls, texts, or appointments were launched.");
      await reload();
    }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"Agency permission review could not be saved.");}
    finally{setBusy("");}
  };
  return <main className="mx-auto max-w-6xl space-y-5 pb-10" data-testid="exclusive-agency-leads-page">
    <header className="rounded-2xl bg-[#0B1B2C] p-5 text-white sm:p-7">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D1A656]">Fortunate Operations / Agency</p>
      <h1 className="mt-2 font-heading text-3xl font-bold">Exclusive Leads</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-300">View prepaid exclusive inquiries delivered into your own agency. Sharing authorization never replaces separate SMS or calling consent.</p>
    </header>
    {error&&<p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice&&<p role="status" className="rounded-lg border bg-white p-3 text-sm">{notice}</p>}
    {loading&&<p className="text-sm">Loading agency leads…</p>}
    {!loading&&rows.length===0&&<p className="rounded-xl border bg-card p-5 text-sm text-muted-foreground">No exclusive leads have been delivered to this agency.</p>}
    {rows.map(lead=>{
      const v=form(lead.id),reviewed=lead.marketplace_agency_consent_review||null;
      const canSave=v.evidence_reference.trim().length>=10 &&
        (!v.agency_sms_permission||v.exact_sms_disclosure.trim().length>=30) &&
        (!v.agency_call_permission||v.exact_call_disclosure.trim().length>=30);
      return <article key={lead.id} className="space-y-4 rounded-xl border bg-card p-4 sm:p-6" data-testid={`exclusive-agency-lead-${lead.id}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-bold">{lead.client_name||"Unnamed inquiry"}</h2>
            <p className="text-xs text-muted-foreground">{lead.display_id||lead.id} · {lead.state||"FL"} · Exclusive agency inquiry</p>
          </div>
          <span className={`rounded-full border px-2.5 py-1 text-xs ${reviewed?"text-emerald-800":"text-amber-800"}`}>
            {reviewed?"Permission review recorded":"Contact review required"}
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-md bg-secondary p-3 text-sm">
            <p className="text-xs font-semibold text-muted-foreground">Phone</p>
            <p>{lead.phone||"Not provided"}</p>
          </div>
          <div className="rounded-md bg-secondary p-3 text-sm">
            <p className="text-xs font-semibold text-muted-foreground">Email</p>
            <p className="break-all">{lead.email||"Not provided"}</p>
          </div>
        </div>
        {reviewed&&<div className="rounded-lg border bg-secondary p-3 text-xs" data-testid="exclusive-consent-status">
          Most recent review: {reviewed.method} · SMS {reviewed.sms_permission?"allowed":"not authorized"} · Calls {reviewed.call_permission?"allowed":"not authorized"}.
          Autodialing is disabled until separately enabled.
        </div>}
        <div className="space-y-3 rounded-lg border p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4"/>Document independent contact permission</h3>
          <p className="text-xs text-muted-foreground">Enter a real verifiable evidence reference and the exact language seen by this consumer. No proof? Leave both permissions off.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold">Verification method
              <select className={input} value={v.verification_method} onChange={e=>update(lead.id,"verification_method",e.target.value)}>
                <option value="inbound_call">Consumer inbound call</option>
                <option value="consented_reply">Consented reply</option>
                <option value="verified_form">Verified consent form</option>
                <option value="signed_authorization">Signed authorization</option>
              </select>
            </label>
            <label className="text-xs font-semibold">Evidence / record reference
              <input className={input} value={v.evidence_reference} onChange={e=>update(lead.id,"evidence_reference",e.target.value)} data-testid={`exclusive-evidence-${lead.id}`}/>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.agency_sms_permission}
             onChange={e=>update(lead.id,"agency_sms_permission",e.target.checked)}/> <MessageSquare className="h-4 w-4"/>Agency-specific SMS permission verified</label>
          {v.agency_sms_permission&&<label className="block text-xs font-semibold">Exact SMS opt-in disclosure
            <textarea rows={2} className={input} value={v.exact_sms_disclosure} onChange={e=>update(lead.id,"exact_sms_disclosure",e.target.value)}/>
          </label>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.agency_call_permission}
            onChange={e=>update(lead.id,"agency_call_permission",e.target.checked)}/> <Phone className="h-4 w-4"/>Agency-specific call/AI disclosure and permission verified</label>
          {v.agency_call_permission&&<label className="block text-xs font-semibold">Exact call / AI voice disclosure
            <textarea rows={2} className={input} value={v.exact_call_disclosure} onChange={e=>update(lead.id,"exact_call_disclosure",e.target.value)}/>
          </label>}
          <button className={control} type="button" disabled={!!busy||!canSave}
            onClick={()=>review(lead)} data-testid={`exclusive-review-${lead.id}`}>
            {busy===lead.id?"Saving…":"Save verified permissions / no-contact restriction"}
          </button>
        </div>
      </article>;
    })}
    <nav className="flex flex-wrap gap-2" aria-label="Next agency workflows">
      <Link to="/pipeline" className={control}><GitBranch className="mr-2 h-4 w-4"/> Lead pipeline</Link>
      <Link to="/appointments" className={control}><CalendarClock className="mr-2 h-4 w-4"/> Appointment booking</Link>
      <Link to="/followups" className={control}>Policy nurture and follow-ups</Link>
      <Link to="/telephony" className={control}>Telephony and Sophia</Link>
    </nav>
    <p className="text-xs text-muted-foreground">Saving a review does not initiate contact, schedule calls, publish ads, or create an insurance policy.</p>
  </main>;
}
